import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { portalBrandingApi, portalConfigApi, portalExportApi, portalAuditApi } from '../../services/api';
import { usePortalStore, toast } from '../../stores';
import { NexusCard, NexusButton, NexusBadge } from '../../components/nexus-ui';
import { Palette, Globe, DownloadSimple, ClockCounterClockwise, PencilSimple, Bell, Lightning } from '@phosphor-icons/react';
import { onboardingApi } from '../../services/onboarding';
import { billingApi } from '../../services/billing';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'ja', label: 'Japanese' },
  { code: 'zh', label: 'Chinese' },
];

const TABS = [
  { id: 'branding',      label: 'Branding',      icon: Palette },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'auto_topup',    label: 'Auto top-up',   icon: Lightning },
  { id: 'language',      label: 'Language',      icon: Globe },
  { id: 'export',        label: 'Export',        icon: DownloadSimple },
  { id: 'audit',         label: 'Audit Log',     icon: ClockCounterClockwise },
];

const PortalSettings = () => {
  const [tab, setTab] = useState('branding');
  const { tenant } = usePortalStore();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  // Deep-link support — e.g. the CreditMeter "Auto top-up" item
  // navigates to /portal/settings?tab=auto_topup and we honour that on
  // mount so the user lands directly on the right pane.
  useEffect(() => {
    const t = params.get('tab');
    if (t && TABS.some((x) => x.id === t)) setTab(t);
  }, [params]);

  return (
    <div className="space-y-6" data-testid="portal-settings">
      <h2 className="font-heading text-2xl font-bold text-nexus-text-primary">Settings</h2>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-nexus-bg-tertiary/50 rounded-lg border border-white/5 w-fit">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            data-testid={`settings-tab-${id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === id ? 'bg-nexus-accent-mint/10 text-nexus-accent-mint' : 'text-nexus-text-tertiary hover:text-nexus-text-secondary'
            }`}
          >
            <Icon className="w-4 h-4" weight="duotone" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'branding' && <BrandingTab />}
      {tab === 'notifications' && <NotificationsTab />}
      {tab === 'auto_topup' && <AutoTopupTab />}
      {tab === 'language' && <LanguageTab />}
      {tab === 'export' && <ExportTab />}
      {tab === 'audit' && <AuditTab />}
    </div>
  );
};

const NOTIFICATION_EVENTS = [
  { key: 'welcome',          label: 'Welcome on signup',         desc: 'Sent once when you create your account.' },
  { key: 'invite_received',  label: 'You were invited',          desc: 'When someone invites you to a Cognara organization.' },
  { key: 'invite_accepted',  label: 'Invite accepted',           desc: 'When a person you invited joins your organization.' },
  { key: 'password_reset',   label: 'Password reset link',       desc: 'Always sent via email when you request a reset.' },
  { key: 'purchase_receipt', label: 'Credit purchase receipt',   desc: 'Confirmation + invoice after every credit top-up.' },
  { key: 'low_balance',      label: 'Low credit balance',        desc: 'Alerts at 50%, 80%, 95% and 100% consumed since last top-up.' },
  { key: 'auto_topup',       label: 'Auto top-up triggered',     desc: 'When the wallet refills automatically below your threshold.' },
  { key: 'invoice_issued',   label: 'New invoice issued',        desc: 'A copy of every invoice when it is generated.' },
  { key: 'weekly_digest',    label: 'Weekly usage digest',       desc: 'Every Monday morning — a quick recap of last week\'s usage.' },
  { key: 'product_updates',  label: 'Product updates & news',    desc: 'New features, model upgrades, important announcements.' },
];

const NotificationsTab = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['notificationPrefs'],
    queryFn: () => onboardingApi.getNotificationPrefs().then((r) => r.data),
  });
  const [draft, setDraft] = useState(null);
  const prefs = draft || data?.prefs || {};

  const setVal = (event, channel, val) => {
    setDraft((d) => ({
      ...(d || data?.prefs || {}),
      [event]: { ...(d?.[event] || data?.prefs?.[event] || {}), [channel]: val },
    }));
  };

  const saveMutation = useMutation({
    mutationFn: (p) => onboardingApi.setNotificationPrefs(p),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationPrefs'] });
      setDraft(null);
      toast.success('Notification preferences saved');
    },
    onError: () => toast.error('Could not save preferences'),
  });

  if (isLoading || !data) return <div className="text-sm text-nexus-text-tertiary">Loading…</div>;

  return (
    <NexusCard variant="glass" hover={false}>
      <div className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading text-lg font-semibold text-nexus-text-primary flex items-center gap-2">
              <Bell className="w-5 h-5" style={{ color: '#F26B1F' }} weight="duotone" />
              Notifications
            </h3>
            <p className="text-[13px] text-nexus-text-secondary mt-1">
              Choose which events alert you in-product (platform) and via email.
            </p>
          </div>
          <NexusButton
            onClick={() => saveMutation.mutate(prefs)}
            isLoading={saveMutation.isPending}
            data-testid="save-notif-prefs">
            Save preferences
          </NexusButton>
        </div>

        <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--hairline)' }}>
          <div className="grid grid-cols-[1fr,80px,80px] gap-3 px-4 py-2 text-[10px] font-semibold tracking-wider uppercase"
            style={{ background: 'var(--layer-2)', color: '#A0AEC0' }}>
            <span>Event</span>
            <span className="text-center">Email</span>
            <span className="text-center">Platform</span>
          </div>
          {NOTIFICATION_EVENTS.map((ev) => {
            const p = prefs[ev.key] || { email: true, platform: true };
            return (
              <div key={ev.key}
                className="grid grid-cols-[1fr,80px,80px] gap-3 px-4 py-3 border-t items-center"
                style={{ borderColor: 'var(--hairline)' }}
                data-testid={`notif-row-${ev.key}`}>
                <div>
                  <div className="text-[13px] font-medium text-nexus-text-primary">{ev.label}</div>
                  <div className="text-[11px] text-nexus-text-tertiary mt-0.5">{ev.desc}</div>
                </div>
                <Toggle on={p.email} disabled={ev.key === 'password_reset'}
                  onChange={(v) => setVal(ev.key, 'email', v)}
                  data-testid={`notif-${ev.key}-email`} />
                <Toggle on={p.platform}
                  onChange={(v) => setVal(ev.key, 'platform', v)}
                  data-testid={`notif-${ev.key}-platform`} />
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-nexus-text-tertiary">
          Password reset emails cannot be turned off — required for account security.
        </p>
      </div>
    </NexusCard>
  );
};

const Toggle = ({ on, onChange, disabled, ...rest }) => (
  <button type="button" onClick={() => !disabled && onChange(!on)} disabled={disabled}
    className={`relative h-5 w-9 rounded-full transition-all mx-auto block ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    style={{ background: on ? '#F26B1F' : 'var(--layer-2)', border: '1px solid var(--hairline)' }}
    aria-pressed={on}
    {...rest}>
    <span className="absolute top-[1px] h-[15px] w-[15px] rounded-full bg-white transition-all"
      style={{ left: on ? '18px' : '2px' }} />
  </button>
);

const AutoTopupTab = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['autoTopup'],
    queryFn: () => billingApi.getAutoTopup().then((r) => r.data),
  });
  const [draft, setDraft] = useState(null);
  const current = draft || data || { enabled: false, threshold_credits: 50, topup_credits: 500 };

  const saveMutation = useMutation({
    mutationFn: (p) => billingApi.setAutoTopup(p).then((r) => r.data),
    onSuccess: (next) => {
      queryClient.setQueryData(['autoTopup'], next);
      setDraft(null);
      toast.success('Auto top-up preferences saved');
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not save auto top-up'),
  });

  if (isLoading || !data) return <div className="text-sm text-nexus-text-tertiary">Loading…</div>;

  const hasPM = data.has_payment_method;
  const hasErr = !!data.last_error;

  return (
    <NexusCard variant="glass" hover={false}>
      <div className="p-6 space-y-5" data-testid="auto-topup-tab">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading text-lg font-semibold text-nexus-text-primary flex items-center gap-2">
              <Lightning className="w-5 h-5" style={{ color: '#F26B1F' }} weight="duotone" />
              Auto top-up
            </h3>
            <p className="text-[13px] text-nexus-text-secondary mt-1 max-w-2xl">
              When your wallet drops below the threshold, Cognara automatically charges the saved card and refills credits.
              Long agent runs and 3D asset analyses never hit a hard stop.
            </p>
          </div>
          <NexusButton
            onClick={() => saveMutation.mutate({
              enabled: !!current.enabled,
              threshold_credits: Math.max(1, Number(current.threshold_credits) || 50),
              topup_credits: Math.max(1, Number(current.topup_credits) || 500),
            })}
            isLoading={saveMutation.isPending}
            data-testid="save-auto-topup">
            Save changes
          </NexusButton>
        </div>

        {/* Saved-card status banner */}
        <div className="rounded-lg p-4"
          style={{
            background: hasPM ? 'rgba(16,185,129,0.06)' : 'rgba(242,107,31,0.06)',
            border: `1px solid ${hasPM ? 'rgba(16,185,129,0.4)' : 'rgba(242,107,31,0.4)'}`,
          }}
          data-testid="auto-topup-pm-status">
          <div className="flex items-start gap-2.5">
            <div className="text-[20px]" style={{ color: hasPM ? '#10B981' : '#F26B1F' }}>
              {hasPM ? '✓' : '!'}
            </div>
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-nexus-text-primary">
                {hasPM ? 'Payment method on file' : 'No payment method saved'}
              </div>
              <div className="text-[11.5px] text-nexus-text-tertiary mt-0.5 leading-relaxed">
                {hasPM
                  ? `Recurring charges ${data.has_recurring_token ? 'are' : 'will be'} processed via Razorpay. Add a new card by buying credits with "Enable Auto Top-Up" ticked.`
                  : 'Buy any credit pack from the Pricing page with the "Enable Auto Top-Up" checkbox ticked to save a card.'}
              </div>
            </div>
          </div>
        </div>

        {/* Threshold + refill form */}
        <div className="rounded-lg p-5" style={{ border: '1px solid var(--hairline)' }}>
          <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-4 items-end">
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 text-nexus-text-tertiary">Refill when balance drops below</div>
              <div className="flex items-center gap-2">
                <input type="number" min="1"
                  value={current.threshold_credits}
                  onChange={(e) => setDraft({ ...current, threshold_credits: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg text-[14px] font-medium outline-none focus:ring-2 text-nexus-text-primary"
                  style={{ background: 'var(--layer-0)', border: '1px solid var(--hairline)' }}
                  data-testid="auto-topup-threshold-input" />
                <span className="text-[11px] text-nexus-text-tertiary whitespace-nowrap">credits</span>
              </div>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 text-nexus-text-tertiary">Refill with</div>
              <div className="flex items-center gap-2">
                <input type="number" min="1"
                  value={current.topup_credits}
                  onChange={(e) => setDraft({ ...current, topup_credits: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg text-[14px] font-medium outline-none focus:ring-2 text-nexus-text-primary"
                  style={{ background: 'var(--layer-0)', border: '1px solid var(--hairline)' }}
                  data-testid="auto-topup-credits-input" />
                <span className="text-[11px] text-nexus-text-tertiary whitespace-nowrap">credits</span>
              </div>
            </label>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-nexus-text-tertiary">Status</span>
              <Toggle on={!!current.enabled} disabled={!hasPM}
                onChange={(v) => setDraft({ ...current, enabled: v })}
                aria-disabled={!hasPM}
                data-testid="auto-topup-enabled-toggle" />
              <span className="text-[10px] text-nexus-text-tertiary mt-0.5">
                {current.enabled ? 'ENABLED' : 'DISABLED'}
              </span>
            </div>
          </div>
        </div>

        {/* Last error / cooldown banner */}
        {hasErr && (
          <div className="rounded-lg p-3 text-[12px]"
            style={{ background: 'rgba(255,80,80,0.06)', border: '1px solid rgba(255,80,80,0.4)', color: '#FF8E8E' }}
            data-testid="auto-topup-last-error">
            <div className="font-semibold mb-0.5">Last charge failed</div>
            <div className="text-[11px] opacity-90">{data.last_error}</div>
            {data.last_failure_at && (
              <div className="text-[10px] opacity-60 mt-1">At {new Date(data.last_failure_at).toLocaleString()}</div>
            )}
          </div>
        )}

        {data.last_topup_at && (
          <p className="text-[11px] text-nexus-text-tertiary">
            Last auto top-up · <b>{new Date(data.last_topup_at).toLocaleString()}</b>
          </p>
        )}
      </div>
    </NexusCard>
  );
};

const BrandingTab = () => {
  const queryClient = useQueryClient();
  const { data: branding, isLoading } = useQuery({
    queryKey: ['portalBranding'],
    queryFn: () => portalBrandingApi.get().then(r => r.data),
  });

  const [form, setForm] = useState(null);

  const saveMutation = useMutation({
    mutationFn: (data) => portalBrandingApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portalBranding'] });
      toast.success('Branding updated');
    },
    onError: () => toast.error('Failed to save branding'),
  });

  const current = form || branding || {};

  const handleChange = (key, value) => {
    setForm(prev => ({ ...(prev || branding || {}), [key]: value }));
  };

  return (
    <NexusCard variant="glass" hover={false}>
      <div className="p-6 space-y-6">
        <h3 className="font-heading text-lg font-semibold text-nexus-text-primary flex items-center gap-2">
          <PencilSimple className="w-5 h-5 text-nexus-accent-mint" weight="duotone" />
          Custom Branding
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm text-nexus-text-secondary mb-1">Logo URL</label>
            <input
              type="url"
              value={current.logo_url || ''}
              onChange={e => handleChange('logo_url', e.target.value)}
              placeholder="https://example.com/logo.png"
              data-testid="branding-logo-url"
              className="w-full bg-nexus-bg-tertiary border border-nexus-border-emphasized rounded-lg px-3 py-2 text-sm text-nexus-text-primary focus:outline-none focus:ring-1 focus:ring-nexus-accent-mint"
            />
          </div>

          <div>
            <label className="block text-sm text-nexus-text-secondary mb-1">Chat Widget Title</label>
            <input
              type="text"
              value={current.chat_widget_title || ''}
              onChange={e => handleChange('chat_widget_title', e.target.value)}
              placeholder="AI Assistant"
              data-testid="branding-widget-title"
              className="w-full bg-nexus-bg-tertiary border border-nexus-border-emphasized rounded-lg px-3 py-2 text-sm text-nexus-text-primary focus:outline-none focus:ring-1 focus:ring-nexus-accent-mint"
            />
          </div>

          <div>
            <label className="block text-sm text-nexus-text-secondary mb-1">Primary Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={current.primary_color || '#1E4E8C'}
                onChange={e => handleChange('primary_color', e.target.value)}
                data-testid="branding-primary-color"
                className="w-10 h-10 rounded-lg cursor-pointer border-0"
              />
              <span className="text-sm text-nexus-text-tertiary">{current.primary_color || '#1E4E8C'}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm text-nexus-text-secondary mb-1">Accent Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={current.accent_color || '#F26B1F'}
                onChange={e => handleChange('accent_color', e.target.value)}
                data-testid="branding-accent-color"
                className="w-10 h-10 rounded-lg cursor-pointer border-0"
              />
              <span className="text-sm text-nexus-text-tertiary">{current.accent_color || '#F26B1F'}</span>
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm text-nexus-text-secondary mb-1">Welcome Message</label>
            <textarea
              value={current.welcome_message || ''}
              onChange={e => handleChange('welcome_message', e.target.value)}
              rows={2}
              placeholder="Welcome to our AI Assistant!"
              data-testid="branding-welcome-message"
              className="w-full bg-nexus-bg-tertiary border border-nexus-border-emphasized rounded-lg px-3 py-2 text-sm text-nexus-text-primary focus:outline-none focus:ring-1 focus:ring-nexus-accent-mint resize-none"
            />
          </div>
        </div>

        {/* Live Preview */}
        <div>
          <p className="text-sm text-nexus-text-secondary mb-2">Preview</p>
          <div className="bg-nexus-bg-tertiary/50 rounded-xl p-4 border border-white/5 max-w-sm">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/5" style={{ borderColor: `${current.primary_color || '#1E4E8C'}30` }}>
              {current.logo_url && <img src={current.logo_url} alt="logo" className="w-6 h-6 rounded" />}
              <span className="text-sm font-semibold" style={{ color: current.primary_color || '#1E4E8C' }}>
                {current.chat_widget_title || 'AI Assistant'}
              </span>
            </div>
            <div className="text-xs text-nexus-text-secondary mb-2">{current.welcome_message || 'Welcome!'}</div>
            <div className="flex gap-2">
              <div className="flex-1 bg-white/5 rounded-lg px-3 py-1.5 text-xs text-nexus-text-tertiary">Type a message...</div>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: current.accent_color || '#F26B1F' }}>
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" /></svg>
              </div>
            </div>
          </div>
        </div>

        <NexusButton
          onClick={() => saveMutation.mutate(form || branding)}
          isLoading={saveMutation.isPending}
          disabled={!form}
          data-testid="save-branding-btn"
        >
          Save Branding
        </NexusButton>
      </div>
    </NexusCard>
  );
};

const LanguageTab = () => {
  const queryClient = useQueryClient();
  const { data: branding } = useQuery({
    queryKey: ['portalBranding'],
    queryFn: () => portalBrandingApi.get().then(r => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: (data) => portalBrandingApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portalBranding'] });
      toast.success('Language updated');
    },
  });

  return (
    <NexusCard variant="glass" hover={false}>
      <div className="p-6 space-y-6">
        <h3 className="font-heading text-lg font-semibold text-nexus-text-primary">Language Settings</h3>

        <div>
          <label className="block text-sm text-nexus-text-secondary mb-2">UI Locale</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => saveMutation.mutate({ ui_locale: lang.code })}
                data-testid={`locale-${lang.code}`}
                className={`p-3 rounded-lg border text-sm font-medium transition-colors text-left ${
                  branding?.ui_locale === lang.code
                    ? 'border-nexus-accent-mint bg-nexus-accent-mint/10 text-nexus-accent-mint'
                    : 'border-nexus-border-emphasized text-nexus-text-secondary hover:border-white/20'
                }`}
              >
                {lang.label}
                <span className="text-xs text-nexus-text-tertiary ml-1">({lang.code})</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm text-nexus-text-secondary mb-2">AI Response Language</label>
          <p className="text-xs text-nexus-text-tertiary mb-3">The language the AI assistant will respond in</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => saveMutation.mutate({ output_language: lang.code })}
                data-testid={`output-lang-${lang.code}`}
                className={`p-3 rounded-lg border text-sm font-medium transition-colors text-left ${
                  branding?.output_language === lang.code
                    ? 'border-nexus-accent-violet bg-nexus-accent-violet/10 text-nexus-accent-violet'
                    : 'border-nexus-border-emphasized text-nexus-text-secondary hover:border-white/20'
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </NexusCard>
  );
};

const ExportTab = () => {
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const res = await portalExportApi.exportAll(format);
      const mimeMap = { json: 'application/json', csv: 'text/csv', pdf: 'application/pdf' };
      const blob = new Blob([res.data], { type: mimeMap[format] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversations.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <NexusCard variant="glass" hover={false}>
      <div className="p-6 space-y-6">
        <h3 className="font-heading text-lg font-semibold text-nexus-text-primary">Export Conversation History</h3>
        <p className="text-sm text-nexus-text-secondary">Download all your conversation history for backup or analysis.</p>

        <div className="flex gap-4 flex-wrap">
          <NexusButton
            onClick={() => handleExport('json')}
            isLoading={exporting}
            variant="secondary"
            data-testid="export-json-btn"
          >
            <DownloadSimple className="w-4 h-4 mr-2" weight="bold" />
            Export as JSON
          </NexusButton>
          <NexusButton
            onClick={() => handleExport('pdf')}
            isLoading={exporting}
            variant="secondary"
            data-testid="export-pdf-btn"
          >
            <DownloadSimple className="w-4 h-4 mr-2" weight="bold" />
            Export as PDF
          </NexusButton>
          <NexusButton
            onClick={() => handleExport('csv')}
            isLoading={exporting}
            variant="secondary"
            data-testid="export-csv-btn"
          >
            <DownloadSimple className="w-4 h-4 mr-2" weight="bold" />
            Export as CSV
          </NexusButton>
        </div>
      </div>
    </NexusCard>
  );
};

const AuditTab = () => {
  const { data } = useQuery({
    queryKey: ['portalAuditLogs'],
    queryFn: () => portalAuditApi.getLogs({ limit: 30 }).then(r => r.data),
  });

  const logs = data?.logs || [];

  return (
    <NexusCard variant="glass" hover={false}>
      <div className="p-6">
        <h3 className="font-heading text-lg font-semibold text-nexus-text-primary mb-4">Activity Log</h3>
        {logs.length === 0 ? (
          <p className="text-sm text-nexus-text-tertiary text-center py-8">No activity logged yet</p>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {logs.map((log) => (
              <div key={log.id} className="flex items-center justify-between p-3 bg-nexus-bg-tertiary/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <NexusBadge variant="default">{log.action?.replace(/_/g, ' ')}</NexusBadge>
                  <div>
                    <p className="text-sm text-nexus-text-primary">{log.target_type}</p>
                    {log.details && <p className="text-xs text-nexus-text-tertiary truncate max-w-sm">{log.details}</p>}
                  </div>
                </div>
                <p className="text-xs text-nexus-text-tertiary flex-shrink-0">
                  {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </NexusCard>
  );
};

export default PortalSettings;
